import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Plus, Edit, Trash2, CheckCircle, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { api } from '../../api';

interface Category {
  _id: string;
  name: string;
  slug: string;
  description: string;
  isActive: boolean;
  createdAt: string;
}

const AdminCategories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  
  const { 
    register, 
    handleSubmit, 
    reset, 
    formState: { errors },
    setError 
  } = useForm<Category>();

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get<Category[]>('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Load categories error:', error);
      toast.error('Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: Category) => {
    try {
      const url = editingCategory 
        ? `/categories/${editingCategory._id}`
        : '/categories';
      const method = editingCategory ? 'put' : 'post';

      const response = await api[method](url, {
        ...data,
        slug: data.slug.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
      });

      toast.success(editingCategory ? 'Catégorie mise à jour' : 'Catégorie créée');
      loadCategories();
      resetForm();
    } catch (error: any) {
      console.error('Save category error:', error);
      
      if (error.response?.data?.errors) {
        error.response.data.errors.forEach((err: any) => {
          setError(err.path, { message: err.msg });
        });
      } else {
        toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
      }
    }
  };

  const deleteCategory = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir désactiver cette catégorie ?')) {
      return;
    }

    try {
      await api.delete(`/categories/${id}`);
      toast.success('Catégorie désactivée');
      loadCategories();
    } catch (error: any) {
      console.error('Delete category error:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la désactivation');
    }
  };

  const resetForm = () => {
    reset();
    setEditingCategory(null);
    setShowForm(false);
  };

  const editCategory = (category: Category) => {
    setEditingCategory(category);
    reset({
      name: category.name,
      slug: category.slug,
      description: category.description,
    });
    setShowForm(true);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Gestion des Catégories</h1>
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Ajouter une catégorie
          </button>
        </div>

        {/* Category Form */}
        {showForm && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom *
                </label>
                <input
                  {...register('name', { 
                    required: 'Le nom est requis',
                    maxLength: {
                      value: 50,
                      message: 'Le nom ne doit pas dépasser 50 caractères'
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="Ex: Analyse GPS"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-brand-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Slug *
                </label>
                <input
                  {...register('slug', { 
                    required: 'Le slug est requis',
                    maxLength: {
                      value: 50,
                      message: 'Le slug ne doit pas dépasser 50 caractères'
                    },
                    pattern: {
                      value: /^[a-z0-9-]+$/,
                      message: 'Seules les lettres minuscules, chiffres et tirets sont autorisés'
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="Ex: gps-analysis"
                />
                {errors.slug && (
                  <p className="mt-1 text-sm text-brand-600">{errors.slug.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  {...register('description', {
                    maxLength: {
                      value: 500,
                      message: 'La description ne doit pas dépasser 500 caractères'
                    }
                  })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="Description de la catégorie..."
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-brand-600">{errors.description.message}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
                >
                  {editingCategory ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Categories List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {categories.length > 0 ? (
                categories.map((category) => (
                  <div key={category._id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          {category.name}
                          {!category.isActive && (
                            <span className="ml-2 px-2 py-1 text-xs bg-brand-100 text-brand-800 rounded">
                              Désactivée
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          {category.description || 'Aucune description'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Slug: {category.slug} | Créée le: {new Date(category.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => editCategory(category)}
                          className="p-2 text-gray-400 hover:text-brand-600 transition-colors"
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteCategory(category._id)}
                          className="p-2 text-gray-400 hover:text-brand-600 transition-colors"
                          title="Désactiver"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-4">
                    <CheckCircle className="h-16 w-16 mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Aucune catégorie trouvée
                  </h3>
                  <p className="text-gray-500">
                    Commencez par créer votre première catégorie
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminCategories;